import * as React from "react"
import { Head, Link, useForm } from "@inertiajs/react"
import { Plus, Trash2, Upload, X } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  type AccountOption,
  LINE_STATUS_LABELS,
  type LineStatus,
} from "@/lib/accountDirectives"
import { SkuMappingCombobox } from "@/components/accounts/SkuMappingCombobox"

type MetricPair = { key: string; value: string }

type LineDraft = {
  id: number | null
  _destroy: boolean
  raw_product_description: string
  account_sku_code: string
  product_code: string
  product_dist_code: string
  threshold_value: string
  threshold_unit: string
  line_status: LineStatus
  metrics: MetricPair[]
}

type DirectiveFormData = {
  id: number | null
  account_id: number | null
  ref_no: string | null
  issued_on: string | null
  subject: string | null
  brand: string | null
  supplier_entity: string | null
  supplier_pic: string | null
  signatories: { name: string; role: string }[]
  grace_deadline: string | null
  review_on: string | null
  flush_out_on: string | null
  discontinue_on: string | null
  clear_conditions: string | null
  consequence_summary: string | null
  document_filename: string | null
  lines: {
    id: number | null
    raw_product_description: string
    account_sku_code: string | null
    product_code: string | null
    product_dist_code: string | null
    threshold_value: string | null
    threshold_unit: string | null
    line_status: LineStatus
    metrics: Record<string, string>
  }[]
}

type PageProps = {
  directive: DirectiveFormData
  account_options: AccountOption[]
  mode: "new" | "edit"
}

type FormState = {
  account_id: string
  ref_no: string
  issued_on: string
  subject: string
  brand: string
  supplier_entity: string
  supplier_pic: string
  signatories: { name: string; role: string }[]
  grace_deadline: string
  review_on: string
  flush_out_on: string
  discontinue_on: string
  clear_conditions: string
  consequence_summary: string
  lines: LineDraft[]
  source_document: File | null
}

function metricsToPairs(metrics: Record<string, string>): MetricPair[] {
  return Object.entries(metrics).map(([key, value]) => ({ key, value: String(value) }))
}

function blankLine(): LineDraft {
  return {
    id: null,
    _destroy: false,
    raw_product_description: "",
    account_sku_code: "",
    product_code: "",
    product_dist_code: "",
    threshold_value: "",
    threshold_unit: "",
    line_status: "warning",
    metrics: [],
  }
}

export default function Form({ directive, account_options, mode }: PageProps) {
  const isEdit = mode === "edit"

  const initialLines: LineDraft[] =
    directive.lines.length > 0
      ? directive.lines.map((l) => ({
          id: l.id,
          _destroy: false,
          raw_product_description: l.raw_product_description ?? "",
          account_sku_code: l.account_sku_code ?? "",
          product_code: l.product_code ?? "",
          product_dist_code: l.product_dist_code ?? "",
          threshold_value: l.threshold_value ?? "",
          threshold_unit: l.threshold_unit ?? "",
          line_status: l.line_status ?? "warning",
          metrics: metricsToPairs(l.metrics ?? {}),
        }))
      : [blankLine()]

  const { data, setData, transform, post, processing, errors } = useForm<FormState>({
    account_id: directive.account_id ? String(directive.account_id) : "",
    ref_no: directive.ref_no ?? "",
    issued_on: directive.issued_on ?? "",
    subject: directive.subject ?? "",
    brand: directive.brand ?? "",
    supplier_entity: directive.supplier_entity ?? "",
    supplier_pic: directive.supplier_pic ?? "",
    signatories: directive.signatories.length > 0 ? directive.signatories : [],
    grace_deadline: directive.grace_deadline ?? "",
    review_on: directive.review_on ?? "",
    flush_out_on: directive.flush_out_on ?? "",
    discontinue_on: directive.discontinue_on ?? "",
    clear_conditions: directive.clear_conditions ?? "",
    consequence_summary: directive.consequence_summary ?? "",
    lines: initialLines,
    source_document: null,
  })

  // --- Line helpers ---
  const updateLine = (idx: number, patch: Partial<LineDraft>) => {
    setData(
      "lines",
      data.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    )
  }
  const addLine = () => setData("lines", [...data.lines, blankLine()])
  const removeLine = (idx: number) => {
    const line = data.lines[idx]
    if (line.id) {
      // Persisted line: mark for destruction so the server deletes it.
      updateLine(idx, { _destroy: true })
    } else {
      setData(
        "lines",
        data.lines.filter((_, i) => i !== idx),
      )
    }
  }

  const updateMetric = (lineIdx: number, metricIdx: number, patch: Partial<MetricPair>) => {
    const line = data.lines[lineIdx]
    const metrics = line.metrics.map((m, i) => (i === metricIdx ? { ...m, ...patch } : m))
    updateLine(lineIdx, { metrics })
  }
  const addMetric = (lineIdx: number) => {
    const line = data.lines[lineIdx]
    updateLine(lineIdx, { metrics: [...line.metrics, { key: "", value: "" }] })
  }
  const removeMetric = (lineIdx: number, metricIdx: number) => {
    const line = data.lines[lineIdx]
    updateLine(lineIdx, { metrics: line.metrics.filter((_, i) => i !== metricIdx) })
  }

  // --- Signatory helpers ---
  const addSignatory = () => setData("signatories", [...data.signatories, { name: "", role: "" }])
  const updateSignatory = (idx: number, patch: Partial<{ name: string; role: string }>) =>
    setData(
      "signatories",
      data.signatories.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    )
  const removeSignatory = (idx: number) =>
    setData(
      "signatories",
      data.signatories.filter((_, i) => i !== idx),
    )

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    transform((form) => {
      const lines_attributes = form.lines.map((l) => {
        const metrics: Record<string, string> = {}
        l.metrics.forEach((m) => {
          if (m.key.trim() !== "") metrics[m.key.trim()] = m.value
        })
        return {
          id: l.id ?? undefined,
          _destroy: l._destroy,
          raw_product_description: l.raw_product_description,
          account_sku_code: l.account_sku_code,
          product_code: l.product_code,
          product_dist_code: l.product_dist_code,
          threshold_value: l.threshold_value,
          threshold_unit: l.threshold_unit,
          line_status: l.line_status,
          metrics,
        }
      })

      const directivePayload: Record<string, unknown> = {
        account_id: form.account_id,
        ref_no: form.ref_no,
        issued_on: form.issued_on,
        subject: form.subject,
        brand: form.brand,
        supplier_entity: form.supplier_entity,
        supplier_pic: form.supplier_pic,
        signatories: form.signatories,
        grace_deadline: form.grace_deadline,
        review_on: form.review_on,
        flush_out_on: form.flush_out_on,
        discontinue_on: form.discontinue_on,
        clear_conditions: form.clear_conditions,
        consequence_summary: form.consequence_summary,
        lines_attributes,
      }
      // Only send the file when a new one was chosen, so an edit without a new
      // upload keeps the existing attachment instead of clearing it.
      if (form.source_document) directivePayload.source_document = form.source_document

      const payload: Record<string, unknown> = { directive: directivePayload }
      // Rails method spoofing: browsers can't PUT multipart, so post with
      // _method=put and Rack::MethodOverride routes it to #update.
      if (isEdit) payload._method = "put"

      return payload as unknown as FormState
    })

    const url = isEdit
      ? `/admin/accounts/directives/${directive.id}`
      : "/admin/accounts/directives"
    post(url, { forceFormData: true })
  }

  const title = isEdit ? "Edit Peringatan" : "Tambah Peringatan"
  const description = "Catat memo peringatan akun→SKU: header memo, baris SKU, dan dokumen sumber."
  const errorMessages = Object.values(errors).filter(Boolean)

  const visibleLines = data.lines
    .map((line, idx) => ({ line, idx }))
    .filter(({ line }) => !line._destroy)

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <Link href="/admin/accounts/directives" className="hover:text-ink-body">
            Peringatan
          </Link>
          <span>/</span>
          <span className="text-ink-body font-medium">{title}</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <h1>{title}</h1>
          <p className="mt-1 text-sm text-ink-muted">{description}</p>
        </div>

        {errorMessages.length > 0 && (
          <div className="mt-6 rounded-lg border border-danger bg-danger-faded p-4">
            <div className="text-sm font-medium text-danger">Mohon perbaiki:</div>
            <ul className="mt-1 list-disc pl-5 text-sm text-danger">
              {errorMessages.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-6 pb-24">
          {/* Memo header */}
          <section className="rounded-lg border border-hairline bg-surface p-5">
            <div className="text-sm font-medium text-ink-muted">Header Memo</div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="account_id">Akun</label>
                <Select
                  id="account_id"
                  className="mt-1"
                  value={data.account_id}
                  onChange={(e) => setData("account_id", e.target.value)}
                >
                  <option value="">— Pilih akun —</option>
                  {account_options.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label htmlFor="ref_no">No. referensi</label>
                <Input
                  id="ref_no"
                  className="mt-1"
                  value={data.ref_no}
                  onChange={(e) => setData("ref_no", e.target.value)}
                  placeholder="cth. 1328/E/MD/GMS1/04/2026"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="subject">Subjek</label>
                <Input
                  id="subject"
                  className="mt-1"
                  value={data.subject}
                  onChange={(e) => setData("subject", e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="brand">Brand</label>
                <Input
                  id="brand"
                  className="mt-1"
                  value={data.brand}
                  onChange={(e) => setData("brand", e.target.value)}
                  placeholder="cth. Do Re Mi"
                />
              </div>
              <div>
                <label htmlFor="issued_on">Tanggal terbit</label>
                <Input
                  id="issued_on"
                  type="date"
                  className="mt-1"
                  value={data.issued_on}
                  onChange={(e) => setData("issued_on", e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="supplier_entity">Entitas supplier</label>
                <Input
                  id="supplier_entity"
                  className="mt-1"
                  value={data.supplier_entity}
                  onChange={(e) => setData("supplier_entity", e.target.value)}
                  placeholder="cth. PT Unza Vitalis"
                />
              </div>
              <div>
                <label htmlFor="supplier_pic">PIC supplier (Up.)</label>
                <Input
                  id="supplier_pic"
                  className="mt-1"
                  value={data.supplier_pic}
                  onChange={(e) => setData("supplier_pic", e.target.value)}
                />
              </div>
            </div>

            {/* Signatories */}
            <div className="mt-5 border-t border-hairline pt-4">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-ink-muted">Penanda tangan (sisi akun)</div>
                <Button type="button" variant="ghost" size="sm" onClick={addSignatory}>
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Tambah
                </Button>
              </div>
              {data.signatories.length === 0 ? (
                <p className="mt-2 text-sm text-ink-muted">Belum ada penanda tangan.</p>
              ) : (
                <div className="mt-3 flex flex-col gap-2">
                  {data.signatories.map((s, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        aria-label="Nama penanda tangan"
                        value={s.name}
                        onChange={(e) => updateSignatory(i, { name: e.target.value })}
                        placeholder="Nama"
                      />
                      <Input
                        aria-label="Jabatan penanda tangan"
                        value={s.role}
                        onChange={(e) => updateSignatory(i, { role: e.target.value })}
                        placeholder="Jabatan"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSignatory(i)}
                        aria-label="Hapus penanda tangan"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Consequence timeline */}
          <section className="rounded-lg border border-hairline bg-surface p-5">
            <div className="text-sm font-medium text-ink-muted">Tenggat & Konsekuensi</div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label htmlFor="grace_deadline">Masa tenggang</label>
                <Input
                  id="grace_deadline"
                  type="date"
                  className="mt-1"
                  value={data.grace_deadline}
                  onChange={(e) => setData("grace_deadline", e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="review_on">Tinjauan</label>
                <Input
                  id="review_on"
                  type="date"
                  className="mt-1"
                  value={data.review_on}
                  onChange={(e) => setData("review_on", e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="flush_out_on">Flush-out</label>
                <Input
                  id="flush_out_on"
                  type="date"
                  className="mt-1"
                  value={data.flush_out_on}
                  onChange={(e) => setData("flush_out_on", e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="discontinue_on">Discontinue</label>
                <Input
                  id="discontinue_on"
                  type="date"
                  className="mt-1"
                  value={data.discontinue_on}
                  onChange={(e) => setData("discontinue_on", e.target.value)}
                />
              </div>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="clear_conditions">Syarat pemulihan</label>
                <textarea
                  id="clear_conditions"
                  className="form-control mt-1"
                  rows={3}
                  value={data.clear_conditions}
                  onChange={(e) => setData("clear_conditions", e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="consequence_summary">Ringkasan konsekuensi</label>
                <textarea
                  id="consequence_summary"
                  className="form-control mt-1"
                  rows={3}
                  value={data.consequence_summary}
                  onChange={(e) => setData("consequence_summary", e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* SKU lines */}
          <section className="rounded-lg border border-hairline bg-surface p-5">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-ink-muted">Baris SKU</div>
              <Button type="button" variant="secondary" size="sm" onClick={addLine}>
                <Plus className="mr-1 h-3.5 w-3.5" />
                Tambah SKU
              </Button>
            </div>

            <div className="mt-4 flex flex-col gap-4">
              {visibleLines.map(({ line, idx }, displayIdx) => (
                <div key={idx} className="rounded-lg border border-hairline p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium text-ink-muted">SKU #{displayIdx + 1}</div>
                    {visibleLines.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLine(idx)}
                        aria-label="Hapus baris SKU"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label htmlFor={`line-desc-${idx}`}>Deskripsi produk</label>
                      <Input
                        id={`line-desc-${idx}`}
                        className="mt-1"
                        value={line.raw_product_description}
                        onChange={(e) => updateLine(idx, { raw_product_description: e.target.value })}
                        placeholder="Teks bebas dari memo"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor={`line-sku-${idx}`}>SKU STAR (pemetaan)</label>
                      <div className="mt-1">
                        <SkuMappingCombobox
                          id={`line-sku-${idx}`}
                          value={line.product_code || null}
                          onChange={(code) => updateLine(idx, { product_code: code ?? "" })}
                        />
                      </div>
                      <p className="mt-1 text-xs text-ink-muted">
                        Petakan ke master STAR untuk menautkan tren performa. Opsional.
                      </p>
                    </div>
                    <div>
                      <label htmlFor={`line-dist-${idx}`}>Kode distributor produk</label>
                      <Input
                        id={`line-dist-${idx}`}
                        className="mt-1"
                        value={line.product_dist_code}
                        onChange={(e) => updateLine(idx, { product_dist_code: e.target.value })}
                        placeholder="Opsional (kunci join sekunder)"
                      />
                    </div>
                    <div>
                      <label htmlFor={`line-plu-${idx}`}>Kode SKU/PLU akun</label>
                      <Input
                        id={`line-plu-${idx}`}
                        className="mt-1"
                        value={line.account_sku_code}
                        onChange={(e) => updateLine(idx, { account_sku_code: e.target.value })}
                      />
                    </div>
                    <div>
                      <label htmlFor={`line-status-${idx}`}>Status baris</label>
                      <Select
                        id={`line-status-${idx}`}
                        className="mt-1"
                        value={line.line_status}
                        onChange={(e) =>
                          updateLine(idx, { line_status: e.target.value as LineStatus })
                        }
                      >
                        {Object.entries(LINE_STATUS_LABELS).map(([v, label]) => (
                          <option key={v} value={v}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <label htmlFor={`line-threshold-${idx}`}>Nilai threshold</label>
                      <Input
                        id={`line-threshold-${idx}`}
                        type="number"
                        step="any"
                        className="mt-1"
                        value={line.threshold_value}
                        onChange={(e) => updateLine(idx, { threshold_value: e.target.value })}
                      />
                    </div>
                    <div>
                      <label htmlFor={`line-unit-${idx}`}>Satuan threshold</label>
                      <Input
                        id={`line-unit-${idx}`}
                        className="mt-1"
                        value={line.threshold_unit}
                        onChange={(e) => updateLine(idx, { threshold_unit: e.target.value })}
                        placeholder="cth. pcs/bulan"
                      />
                    </div>
                  </div>

                  {/* Metric key/value repeater */}
                  <div className="mt-4 border-t border-hairline pt-3">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-medium text-ink-muted">
                        Metrik (JTD, JTJ, %JTJ, SPD, sales bulanan, dll.)
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => addMetric(idx)}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Metrik
                      </Button>
                    </div>
                    {line.metrics.length === 0 ? (
                      <p className="mt-2 text-sm text-ink-muted">Belum ada metrik.</p>
                    ) : (
                      <div className="mt-2 flex flex-col gap-2">
                        {line.metrics.map((m, mi) => (
                          <div key={mi} className="flex gap-2">
                            <Input
                              aria-label="Nama metrik"
                              value={m.key}
                              onChange={(e) => updateMetric(idx, mi, { key: e.target.value })}
                              placeholder="cth. JTD"
                            />
                            <Input
                              aria-label="Nilai metrik"
                              value={m.value}
                              onChange={(e) => updateMetric(idx, mi, { value: e.target.value })}
                              placeholder="Nilai"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeMetric(idx, mi)}
                              aria-label="Hapus metrik"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Source document */}
          <section className="rounded-lg border border-hairline bg-surface p-5">
            <div className="text-sm font-medium text-ink-muted">Dokumen sumber (PDF/email)</div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body hover:bg-surface">
                <Upload className="h-4 w-4" />
                Pilih file
                <input
                  type="file"
                  accept="application/pdf,.pdf,.eml,.msg,image/*"
                  className="hidden"
                  onChange={(e) => setData("source_document", e.target.files?.[0] ?? null)}
                />
              </label>
              {data.source_document ? (
                <span className="text-sm text-ink-body">{data.source_document.name}</span>
              ) : directive.document_filename ? (
                <span className="text-sm text-ink-muted">
                  Terlampir: {directive.document_filename} (pilih file baru untuk mengganti)
                </span>
              ) : (
                <span className="text-sm text-ink-muted">Belum ada dokumen.</span>
              )}
            </div>
          </section>

          {/* Sticky actions */}
          <div className="fixed inset-x-0 bottom-0 z-10 border-t border-hairline bg-surface/95 px-4 py-3 backdrop-blur sm:px-6">
            <div className="mx-auto flex max-w-5xl items-center justify-end gap-2">
              <Button asChild type="button" variant="ghost">
                <Link href="/admin/accounts/directives">Batal</Link>
              </Button>
              <Button type="submit" disabled={processing}>
                {isEdit ? "Simpan perubahan" : "Simpan peringatan"}
              </Button>
            </div>
          </div>
        </form>
      </AppShell>
    </>
  )
}
